// Yönetim: öğretmen ekleme, plan ve abonelik durumu, askıya alma, şifre sıfırlama.
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LuArrowLeft, LuUserPlus } from 'react-icons/lu';
import { ACCOUNT_STATUSES, PLANS, PLAN_IDS, STATUS_LABELS, type AccountStatus, type AdminPlanRequest, type AdminTeacher, type PlanId } from '../../shared/plans';
import { CenterCard, NovaMark } from '../components/ui';
import { api, useMe } from '../lib/api';
import { copyText, formatWhen } from '../lib/util';

interface Issued {
  email: string;
  password: string;
}

function IssuedPassword({ issued, onClose }: { issued: Issued; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="issued" role="status">
      <strong>{issued.email} için geçici şifre</strong>
      <code id="issued-password">{issued.password}</code>
      <p className="muted small">Bu şifre yalnızca şimdi gösterilir. Öğretmene güvenli bir yoldan iletin; ilk girişte kendi şifresini belirleyecek.</p>
      <div className="issued-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={async () => {
            if (await copyText(issued.password)) setCopied(true);
          }}
        >
          {copied ? 'Kopyalandı' : 'Kopyala'}
        </button>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Kapat
        </button>
      </div>
    </div>
  );
}

export function Admin() {
  const { me } = useMe();
  const [teachers, setTeachers] = useState<AdminTeacher[] | null>(null);
  const [requests, setRequests] = useState<AdminPlanRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<PlanId>('deneme');
  const [busy, setBusy] = useState(false);
  const isAdmin = me?.user?.isAdmin === true && !me.user.mustChangePassword;

  const load = useCallback(() => {
    api<{ teachers: AdminTeacher[] }>('/api/admin/teachers').then(
      (r) => setTeachers(r.teachers),
      (err: Error) => setError(err.message),
    );
    api<{ requests: AdminPlanRequest[] }>('/api/admin/plan-requests').then(
      (r) => setRequests(r.requests),
      (err: Error) => setError(err.message),
    );
  }, []);
  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const run = async (task: () => Promise<unknown>) => {
    setError(null);
    try {
      await task();
      load();
    } catch (err) {
      setError((err as Error).message);
      load();
    }
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await run(async () => {
      const r = await api<{ tempPassword: string }>('/api/admin/teachers', { method: 'POST', body: { email, name, plan } });
      setIssued({ email: email.trim().toLowerCase(), password: r.tempPassword });
      setEmail('');
      setName('');
    });
    setBusy(false);
  };

  const patch = (t: AdminTeacher, body: Record<string, unknown>) => run(() => api(`/api/admin/teachers/${t.userId}`, { method: 'PATCH', body }));
  const resolve = (r: AdminPlanRequest, action: 'approve' | 'decline') => {
    const question = action === 'approve' ? `${r.name} hesabı ${PLANS[r.plan].label} paketine geçirilsin mi? Öğretmene e-posta gider.` : `${r.name} talebi kapatılsın mı? Öğretmene e-posta gitmez.`;
    if (!window.confirm(question)) return;
    void run(() => api(`/api/admin/plan-requests/${r.id}/${action}`, { method: 'POST' }));
  };
  const reset = (t: AdminTeacher) => {
    if (!window.confirm(`${t.email} için yeni geçici şifre oluşturulsun mu? Mevcut oturumları kapanır.`)) return;
    void run(async () => {
      const r = await api<{ tempPassword: string }>(`/api/admin/teachers/${t.userId}/reset-password`, { method: 'POST' });
      setIssued({ email: t.email, password: r.tempPassword });
    });
  };

  if (!me) return <CenterCard title="Yükleniyor…" />;
  if (!isAdmin) {
    return (
      <CenterCard title="Bu sayfa için yetkiniz yok" text="Yönetim sayfası yalnızca yönetici hesaplarına açıktır.">
        <a className="btn btn-primary" href="/">
          Ana sayfa
        </a>
      </CenterCard>
    );
  }

  return (
    <div className="home home-wide">
      <header className="home-header">
        <div className="brand">
          <NovaMark size={34} />
          <span className="brand-name">Nova Class · Yönetim</span>
        </div>
        <a className="btn" href="/">
          <LuArrowLeft /> Panele dön
        </a>
      </header>

      <form className="card admin-form" onSubmit={create}>
        <div className="card-title">
          <LuUserPlus /> <h2>Öğretmen ekle</h2>
        </div>
        <div className="admin-form-row">
          <label className="field">
            <span>Ad Soyad</span>
            <input id="teacher-name" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>E-posta</span>
            <input id="teacher-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="field">
            <span>Plan</span>
            <select id="teacher-plan" value={plan} onChange={(e) => setPlan(e.target.value as PlanId)}>
              {PLAN_IDS.map((p) => (
                <option key={p} value={p}>
                  {PLANS[p].label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Ekle
          </button>
        </div>
      </form>

      {issued && <IssuedPassword issued={issued} onClose={() => setIssued(null)} />}
      {error && <p className="form-error admin-error">{error}</p>}

      {requests.length > 0 && (
        <section className="card requests">
          <h2>
            Paket talepleri <span className="notify-count">{requests.length}</span>
          </h2>
          {requests.map((r) => (
            <article key={r.id} className="request">
              <div className="request-head">
                <strong>{r.name}</strong>
                <span className="muted small">
                  {PLANS[r.currentPlan].label} → {PLANS[r.plan].label} · {formatWhen(r.createdAt)}
                </span>
              </div>
              <dl className="request-facts">
                <dt>E-posta</dt>
                <dd>
                  <a href={`mailto:${r.email}`}>{r.email}</a>
                </dd>
                {r.phone && (
                  <>
                    <dt>Telefon</dt>
                    <dd>
                      <a href={`tel:${r.phone.replace(/[^0-9+]/g, '')}`}>{r.phone}</a>
                    </dd>
                  </>
                )}
                {r.institution && (
                  <>
                    <dt>Kurum</dt>
                    <dd>{r.institution}</dd>
                  </>
                )}
                {r.studentCount !== null && (
                  <>
                    <dt>Öğrenci</dt>
                    <dd>{r.studentCount}</dd>
                  </>
                )}
                {r.message && (
                  <>
                    <dt>Mesaj</dt>
                    <dd className="request-text">{r.message}</dd>
                  </>
                )}
              </dl>
              <div className="request-actions">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => resolve(r, 'approve')}>
                  {PLANS[r.plan].label} paketine geçir
                </button>
                <button type="button" className="btn btn-sm" onClick={() => resolve(r, 'decline')}>
                  Talebi kapat
                </button>
              </div>
            </article>
          ))}
        </section>
      )}

      <section className="card recent">
        <h2>Öğretmenler</h2>
        {teachers === null && !error && <p className="muted small">Yükleniyor…</p>}
        {teachers && (
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Öğretmen</th>
                  <th>Plan</th>
                  <th>Abonelik</th>
                  <th>Bitiş</th>
                  <th>Ders</th>
                  <th>Son giriş</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {teachers.map((t) => {
                  const self = t.userId === me.user?.id;
                  return (
                    <tr key={t.userId} className={t.active ? '' : 'is-off'}>
                      <td>
                        <strong>{t.name}</strong>
                        {t.isAdmin && <span className="plan-status is-ok">Yönetici</span>}
                        {!t.emailVerified && <span className="plan-status is-off">E-posta doğrulanmadı</span>}
                        <br />
                        <span className="muted small">{t.email}</span>
                      </td>
                      <td>
                        <select aria-label={`${t.email} planı`} value={t.plan} onChange={(e) => void patch(t, { plan: e.target.value })}>
                          {PLAN_IDS.map((p) => (
                            <option key={p} value={p}>
                              {PLANS[p].label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select aria-label={`${t.email} abonelik durumu`} value={t.status} disabled={self} onChange={(e) => void patch(t, { status: e.target.value as AccountStatus })}>
                          {ACCOUNT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="date"
                          aria-label={`${t.email} bitiş tarihi`}
                          value={t.periodEndMs ? new Date(t.periodEndMs).toISOString().slice(0, 10) : ''}
                          onChange={(e) => void patch(t, { periodEndMs: e.target.value ? Date.parse(`${e.target.value}T23:59:59`) : null })}
                        />
                      </td>
                      <td>{t.lessonCount}</td>
                      <td className="small">{t.lastLoginMs ? formatWhen(t.lastLoginMs) : '—'}</td>
                      <td className="admin-actions">
                        {!self && (
                          <>
                            <button type="button" className="btn btn-sm" onClick={() => reset(t)}>
                              Şifre sıfırla
                            </button>
                            <button type="button" className="btn btn-sm" onClick={() => void patch(t, { active: !t.active })}>
                              {t.active ? 'Girişi kapat' : 'Girişi aç'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
